import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryModal from "@/components/EnquiryModal";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BadgeCheck,
  MapPin,
  BedDouble,
  Ruler,
  Sparkles,
  Building2,
  CalendarClock,
  Hotel,
  ShieldCheck,
  ArrowRight,
  MessageCircle,
  Wine,
  Waves,
  Dumbbell,
  Trees,
  ConciergeBell,
  Sparkle,
  Lock,
  Compass,
  LayoutGrid,
  TrendingUp,
  ShoppingBag,
  Store,
  Dice5,
  Umbrella,
  Gamepad2,
  Flame,
  Car,
  Utensils,
  HeartPulse,
  Plane,
  Droplets,
  Baby,
  Award,
  Users,
  Home as HomeIcon,
  Wrench,
  Phone,
  Landmark,
  Percent,
  Sofa,
} from "lucide-react";
import { AssuredRentalBox } from "@/components/AssuredRental";
import { formatRental } from "@/lib/api";

const PROJECT_META = {
  "ghd-velvet-vista": {
    tagline: "Luxury Studio Apartments in North Goa",
    hero_image: "https://ghdinfra.com/wp-content/uploads/2025/12/VELVET-1.webp",
    regenta_logo:
      "https://ghdinfra.com/wp-content/uploads/2025/12/Regenta-Velvet-porvorim-Logo-300x150.png",
    front_elevation:
      "https://i0.wp.com/ghdinfra.com/wp-content/uploads/2025/12/Rectangle_34627045-1.webp?fit=896%2C745&ssl=1",
    location_map:
      "https://i0.wp.com/ghdinfra.com/wp-content/uploads/2025/12/Velvet_vista_location_map_1-scaled.webp?fit=2560%2C2030&ssl=1",
    floor_plan_image:
      "https://ghdinfra.com/wp-content/uploads/2025/12/Standard-Studio-scaled.webp",
    gallery: [
      "https://ghdinfra.com/wp-content/uploads/2025/12/1-1.webp",
      "https://ghdinfra.com/wp-content/uploads/2025/12/2-1.webp",
      "https://ghdinfra.com/wp-content/uploads/2025/12/3-1.webp",
      "https://ghdinfra.com/wp-content/uploads/2025/12/4.webp",
      "https://ghdinfra.com/wp-content/uploads/2025/12/5.webp",
      "https://ghdinfra.com/wp-content/uploads/2025/12/6.webp",
      "https://ghdinfra.com/wp-content/uploads/2025/12/7.webp",
    ],
    highlights: [
      {
        icon: "layout",
        title: "Modern Living",
        text:
          "Thoughtfully-designed luxury studio apartments with premium layouts crafted for comfort, functionality and smart investment.",
      },
      {
        icon: "compass",
        title: "Prime Address",
        text:
          "Strategically located in North Goa, offering seamless connectivity, lifestyle convenience and strong rental demand.",
      },
      {
        icon: "trending",
        title: "Upgraded Lifestyle",
        text:
          "Hotel-managed residences with fully furnished interiors, modern amenities and premium serviced-apartment living.",
      },
    ],
    floor_plans: [
      { name: "Standard Studio", block: "Block B2, B4, B5 & B6", area: "575.75 sq ft" },
      { name: "Premium Studio", block: "Block B7, B8", area: "645.84 sq ft" },
      { name: "1 BHK", block: "Block B3", area: "925.70 sq ft" },
      { name: "2 BHK with Terrace", block: "Block B3", area: "1,506.96 sq ft" },
      { name: "1 BHK", block: "Block B2, B3, B5 & B6", area: "925.70 sq ft" },
      { name: "2 BHK without Terrace", block: "—", area: "1,313.21 sq ft" },
    ],
    amenities: [
      { icon: "wine", label: "Bar & Restaurant" },
      { icon: "waves", label: "Swimming Pool" },
      { icon: "dumbbell", label: "Gym" },
      { icon: "trees", label: "Landscaped Gardens" },
      { icon: "concierge", label: "Concierge Services" },
      { icon: "sparkle", label: "Housekeeping" },
      { icon: "lock", label: "24/7 Security" },
    ],
    location_advantages: [
      {
        icon: "shopping",
        title: "Mall de Goa",
        text:
          "One of Goa's leading lifestyle destinations for shopping, dining and entertainment, located close to GHD Velvet Vista Goa.",
      },
      {
        icon: "store",
        title: "Delfino's Mart",
        text:
          "A convenient retail destination for everyday essentials, situated near the project in Porvorim, North Goa.",
      },
      {
        icon: "beach",
        title: "Proximity to North Goa Beaches",
        text:
          "Enjoy quick access to North Goa's popular beaches, cafés, nightlife and leisure destinations from your premium apartment.",
      },
      {
        icon: "casino",
        title: "Casinos",
        text:
          "Located near Goa's renowned casino and entertainment hubs, making it ideal for luxury stays and high rental-yield potential.",
      },
    ],
  },

  "aria-residency": {
    tagline: "Coastal Living, Reimagined",
    hero_image:
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/banners/apt-desktop.webp",
    location_map:
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/map.webp",
    site_stats: [
      { label: "Site Area", value: "5,125 sq.m" },
      { label: "Boutique Residences", value: "78" },
      { label: "Towers", value: "4 (Block A & B)" },
      { label: "RERA", value: "PRGO04262718 · PRGO08262775" },
    ],
    gallery: [
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/banners/apt-desktop.webp",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/overview-1.webp",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/highlights.webp",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/gallery/g4.jpg",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/gallery/g5.jpg",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/gallery/g6.jpg",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/gallery/g7.jpg",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/gallery/g8.jpg",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/gallery/g9.jpg",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/gallery/g10.jpg",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/gallery/g15.jpg",
      "https://elevabyhiranandanigoa.com/Apartments/assets/images/gallery/g16.jpg",
    ],
    highlights: [
      {
        icon: "layout",
        title: "Goa's First Wellness Living Residence",
        text:
          "An exclusive address designed around wellness — Aria Residency is Goa's first-ever wellness-living community with curated amenities for mind, body and soul.",
      },
      {
        icon: "compass",
        title: "78 Boutique Residences · Private Pools",
        text:
          "Expansive 2 & 3 BHK layouts in just 78 exclusive residences across 4 towers (Block A & B). Each residence opens onto a private pool.",
      },
      {
        icon: "trending",
        title: "The Hiranandani Legacy",
        text:
          "Backed by 45+ years of real-estate pedigree, 22,000+ happy families and 100+ awards — the Hiranandani promise behind every residence.",
      },
    ],
    unit_configurations: {
      heading: "Aria Residency — Available Configurations",
      sub: "Block A & B · 2 & 3 BHK layouts, each opening onto a private pool.",
      items: [
        "3 BHK Villament",
        "3 BHK Apartment",
        "3 BHK Penthouse",
        "3 BHK Penthouse Terrace",
        "2 BHK Apartment",
        "2 BHK Penthouse",
        "2 BHK Penthouse Terrace",
        "2 BHK Villament",
      ],
    },
    amenities: [
      { icon: "dumbbell", label: "Gym" },
      { icon: "waves", label: "Lap Pool" },
      { icon: "waves", label: "Infinity Pool" },
      { icon: "baby", label: "Kids Pool" },
      { icon: "droplets", label: "Private Pools" },
      { icon: "gamepad", label: "Indoor Game Zone" },
      { icon: "flame", label: "Sauna" },
      { icon: "wine", label: "Restaurant & Bar" },
      { icon: "trees", label: "Children's Park" },
      { icon: "car", label: "Car Parking" },
    ],
    location_advantages: [
      {
        icon: "beach",
        title: "Calangute Beach — 5 minutes",
        text:
          "Walk to Goa's most popular beach for cafés, shacks and sunset promenades straight from your doorstep.",
      },
      {
        icon: "beach",
        title: "Candolim Beach — 8 minutes",
        text:
          "A quieter stretch of golden sand, premier restaurants and boutique spas within a short drive.",
      },
      {
        icon: "beach",
        title: "Baga Beach — 10 minutes",
        text:
          "North Goa's nightlife and dining capital — beach clubs, live music and bustling cafés, minutes away.",
      },
      {
        icon: "hospital",
        title: "Dr. Dukle's Hospital — 5 minutes",
        text:
          "A trusted multi-speciality hospital for day-to-day and emergency care, five minutes from your residence.",
      },
      {
        icon: "store",
        title: "Decathlon Store — 4 minutes",
        text: "Sports, fitness and outdoor gear a four-minute drive away.",
      },
      {
        icon: "store",
        title: "Newton's Supermarket — 4 minutes",
        text: "Goa's favourite supermarket for everyday groceries and gourmet essentials.",
      },
      {
        icon: "hotel",
        title: "Le Méridien — 5 minutes",
        text: "World-class 5-star hospitality, dining and spa facilities nearby.",
      },
      {
        icon: "hotel",
        title: "Hyatt Centric — 5 minutes",
        text: "A premium hotel address for guests, business meetings and brunches.",
      },
      {
        icon: "restaurant",
        title: "KFC · McDonald's · Starbucks",
        text: "KFC — 2 min, McDonald's — 4 min, Starbucks — 5 min. Everyday indulgences within arm's reach.",
      },
      {
        icon: "airport",
        title: "Manohar International Airport — 30 minutes",
        text: "New Mopa Airport sharply improves North Goa connectivity for NRIs and investors.",
      },
      {
        icon: "airport",
        title: "Dabolim International Airport — 45 minutes",
        text: "The original Goa airport remains a convenient option for South Goa and business travellers.",
      },
    ],
    why_invest: {
      heading: "Why Invest in Goa",
      items: [
        {
          icon: "trending",
          title: "High Rental Income",
          text:
            "Year-round domestic and international tourism keeps holiday-home occupancies strong and nightly rates premium.",
        },
        {
          icon: "layout",
          title: "Limited Land Supply",
          text:
            "CRZ regulations and coastal preservation sharply restrict new supply, protecting long-term asset values.",
        },
        {
          icon: "compass",
          title: "Better Infrastructure",
          text:
            "The new Mopa International Airport, highway upgrades and the Zuari bridge dramatically improve accessibility.",
        },
        {
          icon: "trending",
          title: "Strong Buyer Demand",
          text:
            "NRIs and affluent Indian investors continue to seek Goa addresses for lifestyle and portfolio diversification.",
        },
      ],
    },
    legacy_stats: {
      heading: "The Hiranandani Legacy",
      sub: "Decades of trust, craftsmanship and township expertise behind every Eleva residence.",
      items: [
        { icon: "award", value: "45+", label: "Years in Real Estate" },
        { icon: "users", value: "22,000+", label: "Happy Families" },
        { icon: "trophy", value: "100+", label: "Awards Won" },
        { icon: "wrench", value: "Hi-Tech", label: "Construction Standards" },
        { icon: "home", value: "Proven", label: "Township Expertise" },
      ],
    },
  },

  "cloud-infinity-villas": {
    tagline: "A Distinctive Address in Old Goa",
    hero_image:
      "https://elevabyhiranandanigoa.com/Villas/assets/images/banners/villa-desktop.webp",
    location_map:
      "https://elevabyhiranandanigoa.com/Villas/assets/images/map.webp",
    site_stats: [
      { label: "Site Area", value: "140,081 sq.ft" },
      { label: "Exclusive Villas", value: "44" },
      { label: "Blocks", value: "A to D" },
      { label: "RERA", value: "PRGO03262696" },
    ],
    gallery: [
      "https://elevabyhiranandanigoa.com/Villas/assets/images/banners/villa-desktop.webp",
      "https://elevabyhiranandanigoa.com/Villas/assets/images/overview.jpg",
      "https://elevabyhiranandanigoa.com/Villas/assets/images/h1.jpg",
      "https://elevabyhiranandanigoa.com/Villas/assets/images/gallery/g1.jpg",
      "https://elevabyhiranandanigoa.com/Villas/assets/images/gallery/g2.jpg",
      "https://elevabyhiranandanigoa.com/Villas/assets/images/gallery/g4.jpg",
      "https://elevabyhiranandanigoa.com/Villas/assets/images/gallery/g5.jpg",
      "https://elevabyhiranandanigoa.com/Villas/assets/images/gallery/g7.jpg",
      "https://elevabyhiranandanigoa.com/Villas/assets/images/gallery/g8.jpg",
      "https://elevabyhiranandanigoa.com/Villas/assets/images/gallery/g9.jpg",
    ],
    highlights: [
      {
        icon: "layout",
        title: "Old Goa's New Pinnacle of Curated Luxury",
        text:
          "A distinctive address in Kadamba, New Panjim — 44 exclusive gated villas across Blocks A to D, crafted for buyers who demand privacy, space and craftsmanship.",
      },
      {
        icon: "compass",
        title: "11,000+ sq.ft Clubhouse & Lifestyle",
        text:
          "Integrated clubhouse, dining and aquatic facilities — resort-style pool, retail boulevard, signature café and multipurpose halls, all inside the gated enclave.",
      },
      {
        icon: "trending",
        title: "Expansive 3 & 4 BHK Villa Layouts",
        text:
          "Single-family villas with generous indoor-outdoor living, private landscaping and premium specifications across every residence.",
      },
    ],
    unit_configurations: {
      heading: "Cloud Infinity — Villa Configurations",
      sub: "Blocks A to D · 44 exclusive gated villas with expansive 3 & 4 BHK layouts.",
      items: ["3 BHK Villa", "4 BHK Villa"],
    },
    amenities: [
      { icon: "lock", label: "Gated Enclave" },
      { icon: "lock", label: "24/7 Security" },
      { icon: "car", label: "Residents' Parking" },
      { icon: "sparkle", label: "Uninterrupted Power" },
      { icon: "trees", label: "Verdant Landscapes" },
      { icon: "waves", label: "Resort-Style Pool" },
      { icon: "baby", label: "Kids Adventure Zone" },
      { icon: "dumbbell", label: "Multi-Sports Court" },
      { icon: "trees", label: "Jogging Track" },
      { icon: "dumbbell", label: "Outdoor Fitness Studio" },
      { icon: "flame", label: "Climbing Wall" },
      { icon: "trees", label: "Event Lawns" },
      { icon: "baby", label: "Pet Park" },
      { icon: "wine", label: "Multipurpose Hall" },
      { icon: "dumbbell", label: "State-of-the-Art Gym" },
      { icon: "gamepad", label: "Indoor Recreation" },
      { icon: "shopping", label: "Retail Boulevard" },
      { icon: "wine", label: "Signature Café" },
    ],
    location_advantages: [
      { icon: "compass", title: "New Zuari Bridge — 20 minutes", text: "Dramatically improves North–South Goa connectivity for daily commutes and getaways." },
      { icon: "airport", title: "Dabolim International Airport — 35 minutes", text: "Direct access to Goa's primary international airport for business and leisure travel." },
      { icon: "airport", title: "Manohar International Airport — 45 minutes", text: "The new Mopa airport is a 45-minute drive, giving two-airport access from one address." },
      { icon: "hotel", title: "The Fern Kadamba Hotel & Spa — 4 minutes", text: "Boutique five-star hospitality for guests, weekend brunches and spa visits." },
      { icon: "hotel", title: "DoubleTree By Hilton Goa — 30 minutes", text: "A premium Hilton hotel within easy reach for business and family stays." },
      { icon: "store", title: "Old Goa Educational Institute — 3 minutes", text: "A trusted neighbourhood school just three minutes from the enclave." },
      { icon: "store", title: "Sunshine Worldwide School — 5 minutes", text: "An internationally focused K–12 school a short drive away." },
      { icon: "hospital", title: "Healthway Hospital Old Goa — 2 minutes", text: "A multi-speciality hospital within two minutes for day-to-day and emergency care." },
      { icon: "hospital", title: "Goa Medical College — 15 minutes", text: "The state's largest tertiary-care medical centre, 15 minutes from your villa." },
    ],
    why_invest: {
      heading: "Why Invest in Goa",
      items: [
        { icon: "trending", title: "High Rental Income", text: "Year-round tourism and corporate travel sustain premium holiday-home yields across North and Central Goa." },
        { icon: "layout", title: "Limited Land Supply", text: "Villa-grade plots in Old Goa are increasingly scarce, protecting long-term asset values for early investors." },
        { icon: "compass", title: "Better Infrastructure", text: "The Zuari bridge, highway upgrades and Mopa airport are reshaping Goa's accessibility and economic trajectory." },
        { icon: "trending", title: "Strong Buyer Demand", text: "NRIs and HNIs prefer villa-format homes in Goa for privacy, legacy and discretionary diversification." },
      ],
    },
    legacy_stats: {
      heading: "The Hiranandani Legacy",
      sub: "Decades of trust, craftsmanship and township expertise behind every Eleva residence.",
      items: [
        { icon: "award", value: "45+", label: "Years in Real Estate" },
        { icon: "users", value: "22,000+", label: "Happy Families" },
        { icon: "trophy", value: "100+", label: "Awards Won" },
        { icon: "wrench", value: "Hi-Tech", label: "Construction Standards" },
        { icon: "home", value: "Proven", label: "Township Expertise" },
      ],
    },
  },
};

PROJECT_META["sparv-etereo-1"] = {
  tagline: "Your Goa Dream. Your Future Investment.",
  hero_image: "/projects/sparv-pool.jpg",
  hero_poster: "/projects/sparv-etereo-hero.jpg",
  contact_phone: "+91 91999 95679",
  highlights_heading: "Why SPARV Etereo 1",
  gallery_heading: "Inside Etereo 1",
  amenities_heading: "Resort-style living, every day",
  location_heading: "Marna Village, Siolim — North Goa",
  site_stats: [
    { label: "Residences", value: "Premium 1 & 2 BHK" },
    { label: "Area", value: "810 – 2,100 sq.ft" },
    { label: "RERA", value: "Approved" },
    { label: "Booking", value: "Just 10%" },
  ],
  gallery: [
    "/projects/sparv-pool.jpg",
    "/projects/sparv-living.jpg",
    "/projects/sparv-bedroom.jpg",
    "/projects/sparv-yoga.jpg",
    "/projects/sparv-beach.jpg",
    "/projects/sparv-etereo-hero.jpg",
  ],
  highlights: [
    {
      icon: "layout",
      title: "Premium 1 & 2 BHK Residences",
      text: "Spacious RERA-approved homes from 810 to 2,100 sq.ft with elegant interiors, crafted for living and investing.",
    },
    {
      icon: "trending",
      title: "Assured Monthly Rental",
      text: "1 BHK — ₹55,000/month and 2 BHK — ₹1,05,000/month assured rental, turning your Goa home into steady income.",
    },
    {
      icon: "compass",
      title: "Siolim, North Goa",
      text: "Set in Marna Village, Siolim — 25 minutes from Mopa Airport, in one of North Goa's most sought-after neighbourhoods.",
    },
  ],
  investment_terms: [
    { icon: "percent", title: "Book with Just 10%", text: "Secure your Goa address with only a 10% down payment." },
    { icon: "bank", title: "Loans from ICICI Bank & SBI", text: "Home loans available from ICICI Bank and SBI." },
    { icon: "shield", title: "RERA Approved", text: "RERA-approved project with residences from 810 to 2,100 sq.ft." },
  ],
  amenities: [
    { icon: "waves", label: "Resort-Style Pool" },
    { icon: "trees", label: "Tropical Landscaping" },
    { icon: "sofa", label: "Elegant Interiors" },
    { icon: "dumbbell", label: "Yoga & Wellness Deck" },
    { icon: "sparkle", label: "Resort-Style Amenities" },
    { icon: "home", label: "Spacious 2 BHK" },
  ],
  location_advantages: [
    { icon: "airport", title: "Mopa Airport — 25 minutes", text: "Manohar International Airport is just 25 minutes away, ideal for NRIs and holiday-home guests." },
    { icon: "beach", title: "North Goa Beaches", text: "Siolim places you within the North Goa coastline — a strong draw for holiday rentals." },
    { icon: "pin", title: "Marna Village, Siolim — 403517", text: "Project address: Marna Village, Siolim, North Goa — 403517." },
  ],
};

const TERM_ICONS = { percent: Percent, bank: Landmark, shield: ShieldCheck };

const HIGHLIGHT_ICONS = {
  layout: LayoutGrid,
  compass: Compass,
  trending: TrendingUp,
};

const AMENITY_ICONS = {
  wine: Wine,
  waves: Waves,
  dumbbell: Dumbbell,
  trees: Trees,
  concierge: ConciergeBell,
  sparkle: Sparkle,
  lock: Lock,
  gamepad: Gamepad2,
  flame: Flame,
  car: Car,
  utensils: Utensils,
  droplets: Droplets,
  baby: Baby,
  sofa: Sofa,
  home: HomeIcon,
};

const LOCATION_ICONS = {
  shopping: ShoppingBag,
  store: Store,
  beach: Umbrella,
  casino: Dice5,
  hospital: HeartPulse,
  hotel: Hotel,
  restaurant: Utensils,
  airport: Plane,
};

const LEGACY_ICONS = {
  award: Award,
  users: Users,
  trophy: Award,
  wrench: Wrench,
  home: HomeIcon,
};

export default function ProjectPage() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enquiryFor, setEnquiryFor] = useState(null);
  const [activeGallery, setActiveGallery] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${slug}`);
        setProject(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading)
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-24 animate-pulse">
          <div className="h-96 bg-[#F4F7FB] rounded-xl" />
        </div>
      </div>
    );

  if (!project)
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-24 text-center text-[#1F2A44]">
          Project not found.
        </div>
        <Footer />
      </div>
    );

  const meta = PROJECT_META[slug] || {};
  const desc =
    (lang === "hi" && project.description_hi) || project.description_en;

  const factPill = (icon, label, value) => value ? (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
      <div className="w-10 h-10 shrink-0 rounded-lg bg-[#C9A84C]/15 text-[#C9A84C] flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A84C]">
          {label}
        </div>
        <div className="text-sm text-white font-semibold mt-0.5">{value}</div>
      </div>
    </div>
  ) : null;

  const openEnquiry = (ctx) => setEnquiryFor(ctx);

  return (
    <div className="min-h-screen bg-white text-[#0B2545] font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="project-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${meta.hero_image || ""})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-[#061528]/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#061528] via-[#0B2545]/85 to-[#061528]" />
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 ${meta.hero_poster ? "grid grid-cols-1 lg:grid-cols-3 gap-10 items-center" : ""}`}>
          <div className={meta.hero_poster ? "lg:col-span-2" : ""}>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#C9A84C]/20 border border-[#C9A84C] text-[#E9D58F] text-xs uppercase tracking-[0.24em] font-bold">
              <Sparkles size={13} strokeWidth={3} />
              {t.badges.exclusive}
            </div>
            {meta.regenta_logo && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/95 border border-white/30">
                <img
                  src={meta.regenta_logo}
                  alt="Regenta Hotels & Resorts"
                  data-testid="regenta-logo"
                  className="h-6 w-auto object-contain"
                />
              </div>
            )}
          </div>

          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight max-w-3xl">
            {project.name}
          </h1>
          {meta.tagline && (
            <p className="mt-3 text-[#E9D58F] text-lg md:text-xl font-serif italic">
              {meta.tagline}
            </p>
          )}
          <div className="mt-3 inline-flex items-center text-[#E6ECF5] gap-2">
            <MapPin size={14} className="text-[#C9A84C]" />
            <span>{project.locality}</span>
          </div>
          <p className="mt-6 max-w-3xl text-[#E6ECF5] text-base md:text-lg leading-relaxed">
            {desc}
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl">
            {factPill(<Hotel size={18} />, "Managed by", project.managed_by)}
            {factPill(<ShieldCheck size={18} />, "RERA", project.rera)}
            {factPill(<Building2 size={18} />, "Total Units", project.total_units)}
            {factPill(<CalendarClock size={18} />, "Possession", project.completion)}
          </div>

          {meta.site_stats && (
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs md:text-sm text-white font-semibold">
              {meta.site_stats.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C]" aria-hidden />
                  <span className="text-[#C9A84C] uppercase tracking-[0.18em] text-[10px] md:text-[11px] font-bold">
                    {s.label}
                  </span>
                  <span className="text-white">{s.value}</span>
                </span>
              ))}
            </div>
          )}
          {meta.contact_phone && (
            <a
              href={`tel:${meta.contact_phone.replace(/\s/g, "")}`}
              data-testid="project-hero-call-btn"
              className="mt-8 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-[#C9A84C] text-[#0B2545] hover:bg-[#E9D58F] font-bold uppercase tracking-wider text-xs transition-colors"
            >
              <Phone size={15} strokeWidth={2.75} /> Call {meta.contact_phone}
            </a>
          )}
          </div>
          {meta.hero_poster && (
            <img
              src={meta.hero_poster}
              alt={`${project.name} brochure`}
              data-testid="project-hero-poster"
              className="w-full max-w-sm mx-auto rounded-2xl border-2 border-[#C9A84C] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]"
            />
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <AssuredRentalBox units={project.units} />
      </div>

      {meta.investment_terms && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-testid="project-investment-terms">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {meta.investment_terms.map((term, i) => {
              const Icon = TERM_ICONS[term.icon] || BadgeCheck;
              return (
                <div key={i} data-testid={`investment-term-${i}`} className="flex items-start gap-4 p-5 rounded-2xl border-2 border-[#C9A84C]/50 bg-[#FFFDF5]">
                  <div className="w-11 h-11 shrink-0 rounded-lg bg-[#0B2545] text-[#C9A84C] flex items-center justify-center">
                    <Icon size={20} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="font-serif text-lg text-[#0B2545] font-bold leading-tight">{term.title}</div>
                    <p className="mt-1 text-sm text-[#1F2A44] font-medium">{term.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Assured strip — only when project has at least one assured-return unit */}
      {project.units.some((u) => u.is_assured_return && u.assured_return_pct) && (
      <section className="bg-[#FFFDF5] border-y border-[#DFBF65]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0B2545] text-[#C9A84C] flex items-center justify-center">
              <BadgeCheck size={18} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#854D0E] font-semibold">
                {t.badges.assured}
              </div>
              <div className="font-serif text-xl text-[#0B2545] leading-tight">
                {project.units.find((u) => u.is_assured_return)?.assured_return_pct || 10}% Assured Annual Return — from Day 1
              </div>
            </div>
          </div>
          <Button
            onClick={() => openEnquiry({ title_en: project.name, id: null })}
            data-testid="project-top-enquire-btn"
            className="bg-[#0B2545] text-[#C9A84C] hover:bg-[#133763] hover:text-[#E9D58F] font-semibold uppercase tracking-wider text-xs"
          >
            {t.detail.enquire_now}
            <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </div>
      </section>
      )}

      {/* Key Highlights */}
      {meta.highlights && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16" data-testid="project-highlights">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">
            Key Highlights
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
            {meta.highlights_heading || "Why GHD Velvet Vista"}
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {meta.highlights.map((h, i) => {
              const Icon = HIGHLIGHT_ICONS[h.icon] || Sparkles;
              return (
                <div
                  key={i}
                  data-testid={`highlight-card-${i}`}
                  className="p-6 rounded-2xl border border-[#EBEFF5] bg-white hover:border-[#C9A84C]/60 hover:shadow-[0_14px_40px_-20px_rgba(201,168,76,0.5)] transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#FFFDF5] border border-[#DFBF65]/40 text-[#C9A84C] flex items-center justify-center">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-[#0B2545]">{h.title}</h3>
                  <p className="mt-2 text-sm text-[#1F2A44] leading-relaxed">{h.text}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Gallery */}
      {meta.gallery?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="project-gallery">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">
            Gallery
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
            {meta.gallery_heading || "Inside Velvet Vista"}
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 relative aspect-[16/10] rounded-2xl overflow-hidden bg-[#F4F7FB] border border-[#EBEFF5]">
              <img
                src={meta.gallery[activeGallery]}
                alt={project.name}
                className="w-full h-full object-cover"
                data-testid="gallery-main"
              />
            </div>
            <div className="grid grid-cols-3 md:grid-cols-2 gap-3 content-start">
              {meta.gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveGallery(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`relative aspect-[4/3] rounded-lg overflow-hidden border transition ${
                    i === activeGallery
                      ? "border-[#C9A84C] ring-2 ring-[#C9A84C]/40"
                      : "border-[#EBEFF5] opacity-80 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Units — hide when configurations are listed and there's only one summary unit */}
      {!(meta.unit_configurations && project.units.length <= 1) && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="project-units">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">
          Unit Types & Pricing
        </div>
        <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
          Choose your residence
        </h2>
        <p className="mt-5 text-[#1F2A44] max-w-xl font-medium">
          {project.units.length} thoughtfully-curated option{project.units.length === 1 ? "" : "s"}, professionally overseen by {project.managed_by}.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
          {project.units.map((u) => {
            const title = (lang === "hi" && u.title_hi) || u.title_en;
            const cover = u.image_paths?.[0];
            return (
              <article
                key={u.id}
                data-testid={`project-unit-card-${u.id}`}
                className="group bg-white rounded-2xl overflow-hidden border-2 border-[#C9A84C]/60 shadow-[0_14px_40px_-18px_rgba(201,168,76,0.5)] hover:shadow-[0_20px_55px_-18px_rgba(201,168,76,0.8)] hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <div className="relative aspect-[4/3] bg-[#F4F7FB] overflow-hidden">
                  <img
                    src={cover}
                    alt={title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 flex flex-col gap-2">
                    <span
                      data-testid={`project-badge-exclusive-${u.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase bg-[#0B2545] text-[#C9A84C] shadow-sm"
                    >
                      <Sparkles size={11} /> {t.badges.exclusive}
                    </span>
                    {u.is_assured_return && u.assured_return_pct ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase bg-[#FEF9C3] text-[#854D0E] border border-[#C9A84C]/60 shadow-sm">
                        <BadgeCheck size={11} /> {u.assured_return_pct}% Assured
                      </span>
                    ) : null}
                  </div>
                  <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/95 text-[10px] font-semibold uppercase tracking-wider text-[#0B2545]">
                    <Hotel size={10} /> {project.managed_by?.split(/[ &—-]/)[0] || "Richcube"}
                  </div>
                </div>

                <div className="p-5 flex-1 flex flex-col">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#C9A84C] font-semibold">
                    {u.unit_label}
                  </div>
                  <h3 className="mt-1 font-serif text-lg text-[#0B2545] leading-snug">
                    {u.block || title}
                  </h3>

                  <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-[#1F2A44]">
                    {u.bhk ? (
                      <span className="inline-flex items-center gap-1.5 font-semibold">
                        <BedDouble size={13} className="text-[#0B2545]" />
                        {u.bhk} BHK
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[#0B2545] font-semibold">
                        Studio
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                      <Ruler size={13} className="text-[#0B2545]" />
                      {u.sqft_display || `${u.sqft} sqft`}
                    </span>
                  </div>

                  {u.assured_rental_monthly ? (
                    <div data-testid={`project-unit-rental-${u.id}`} className="mt-4 p-3 rounded-lg bg-[#C9A84C] text-[#0B2545]">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold">
                        {t.rental.label}
                      </div>
                      <div className="mt-0.5 font-serif text-xl font-bold">
                        {formatRental(u.assured_rental_monthly)}{t.rental.per_month_long}
                      </div>
                    </div>
                  ) : null}

                  {u.is_assured_return && u.assured_return_pct ? (
                    <div className="mt-4 p-3 rounded-lg bg-[#FFFDF5] border border-[#DFBF65]/50">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#854D0E] font-bold">
                        Assured Return
                      </div>
                      <div className="mt-0.5 font-serif text-lg text-[#0B2545] font-bold">
                        {u.assured_return_pct}% p.a. from Day 1
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-auto pt-5 border-t-2 border-[#C9A84C]/40 flex items-end justify-between gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#1F2A44] font-bold">
                        Investment
                      </div>
                      <div className="rc-price-gold text-base font-black tracking-tight">
                        Price on Request
                      </div>
                    </div>
                    <button
                      onClick={() => openEnquiry(u)}
                      data-testid={`project-unit-enquire-${u.id}`}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#0B2545] text-[#C9A84C] text-[11px] font-semibold tracking-wider uppercase hover:bg-[#133763] hover:text-[#E9D58F] transition-colors"
                    >
                      {t.card.enquire}
                      <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
      )}

      {/* Available Configurations */}
      {meta.unit_configurations && (
        <section
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20"
          data-testid="project-configurations"
        >
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-bold">
            Configurations
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
            {meta.unit_configurations.heading}
          </h2>
          <p className="mt-5 text-[#1F2A44] max-w-2xl font-medium">
            {meta.unit_configurations.sub}
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {meta.unit_configurations.items.map((item, i) => (
              <div
                key={i}
                data-testid={`config-item-${i}`}
                className="group relative p-4 rounded-xl border-2 border-[#0B2545]/15 bg-white hover:border-[#C9A84C] hover:shadow-[0_14px_40px_-20px_rgba(201,168,76,0.5)] transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-[#FFFDF5] border border-[#DFBF65]/50 text-[#C9A84C] flex items-center justify-center">
                    <HomeIcon size={16} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="font-serif text-base text-[#0B2545] font-bold leading-tight">
                      {item}
                    </div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#1F2A44] font-bold">
                      Price on Request
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {meta.floor_plans && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="project-floor-plans">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">
            Floor Plans
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
            Spacious, efficient, elegant
          </h2>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-[#EBEFF5] bg-[#F4F7FB] p-4">
              <img
                src={meta.floor_plan_image}
                alt="GHD Velvet Vista floor plan"
                data-testid="floor-plan-image"
                className="w-full h-auto object-contain"
              />
              <div className="mt-3 text-center text-xs text-[#1F2A44] uppercase tracking-wider">
                Standard Studio — Representative Floor Plan
              </div>
            </div>
            <div className="lg:col-span-3 rounded-2xl border border-[#EBEFF5] bg-white overflow-hidden">
              <Table data-testid="floor-plan-table">
                <TableHeader>
                  <TableRow className="bg-[#F4F7FB]">
                    <TableHead className="text-[#0B2545] font-semibold">Unit</TableHead>
                    <TableHead className="text-[#0B2545] font-semibold">Block</TableHead>
                    <TableHead className="text-right text-[#0B2545] font-semibold">
                      Carpet Area
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {meta.floor_plans.map((fp, i) => (
                    <TableRow key={i} data-testid={`floor-plan-row-${i}`}>
                      <TableCell className="font-medium text-[#0B2545]">
                        {fp.name}
                      </TableCell>
                      <TableCell className="text-[#1F2A44]">{fp.block}</TableCell>
                      <TableCell className="text-right text-[#0B2545] font-semibold">
                        {fp.area}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </section>
      )}

      {/* Amenities */}
      {meta.amenities && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="project-amenities">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">
            Amenities
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
            {meta.amenities_heading || "Hotel-grade lifestyle, every day"}
          </h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {meta.amenities.map((a, i) => {
              const Icon = AMENITY_ICONS[a.icon] || Sparkle;
              return (
                <div
                  key={i}
                  data-testid={`amenity-tile-${i}`}
                  className="p-4 rounded-xl border border-[#EBEFF5] bg-[#F4F7FB] hover:bg-white hover:border-[#C9A84C]/50 transition-all text-center"
                >
                  <div className="mx-auto w-11 h-11 rounded-full bg-[#0B2545] text-[#C9A84C] flex items-center justify-center">
                    <Icon size={18} />
                  </div>
                  <div className="mt-3 text-xs text-[#0B2545] font-semibold tracking-wide">
                    {a.label}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Location */}
      {(meta.location_map || meta.location_advantages) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="project-location">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-bold">
            Location
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
            {meta.location_heading || "Porvorim, at the centre of North Goa"}
          </h2>
          <div
            className={`mt-8 grid grid-cols-1 ${
              meta.location_map ? "lg:grid-cols-5" : "lg:grid-cols-1"
            } gap-8 items-stretch`}
          >
            {meta.location_map && (
              <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-[#EBEFF5] bg-[#F4F7FB]">
                <img
                  src={meta.location_map}
                  alt="Project location map"
                  data-testid="location-map"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className={meta.location_map ? "lg:col-span-2" : ""}>
              <h3 className="font-serif text-2xl text-[#0B2545] font-bold">
                Everything near you
              </h3>
              <span className="rc-underline-gold" />
              <div
                className={`mt-6 grid grid-cols-1 sm:grid-cols-2 ${
                  meta.location_map ? "lg:grid-cols-1" : "lg:grid-cols-2"
                } gap-4`}
              >
                {meta.location_advantages.map((adv, i) => {
                  const Icon = LOCATION_ICONS[adv.icon] || MapPin;
                  return (
                    <div
                      key={i}
                      data-testid={`loc-adv-${i}`}
                      className="group p-5 rounded-xl bg-white border-2 border-[#0B2545]/15 hover:border-[#C9A84C] hover:shadow-[0_14px_40px_-20px_rgba(201,168,76,0.5)] transition-all"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 shrink-0 rounded-lg bg-[#0B2545] text-[#C9A84C] flex items-center justify-center group-hover:bg-[#C9A84C] group-hover:text-[#0B2545] transition-colors">
                          <Icon size={20} strokeWidth={2.5} />
                        </div>
                        <div>
                          <div className="font-serif text-lg text-[#0B2545] font-bold leading-tight">
                            {adv.title}
                          </div>
                          <p className="mt-1.5 text-sm text-[#1F2A44] leading-relaxed font-medium">
                            {adv.text}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Front elevation callout */}
      {meta.front_elevation && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
          <div className="relative rounded-2xl overflow-hidden border border-[#EBEFF5]">
            <img
              src={meta.front_elevation}
              alt="GHD Velvet Vista front elevation"
              data-testid="front-elevation"
              className="w-full h-auto object-cover"
            />
          </div>
        </section>
      )}

      {/* Why Invest in Goa */}
      {meta.why_invest && (
        <section
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20"
          data-testid="project-why-invest"
        >
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-bold">
            Investment Thesis
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] rc-heading">
            {meta.why_invest.heading}
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {meta.why_invest.items.map((item, i) => {
              const Icon = HIGHLIGHT_ICONS[item.icon] || TrendingUp;
              return (
                <div
                  key={i}
                  data-testid={`why-invest-${i}`}
                  className="p-6 rounded-2xl border-2 border-[#0B2545]/15 bg-white hover:border-[#C9A84C] hover:shadow-[0_14px_40px_-20px_rgba(201,168,76,0.5)] transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#0B2545] text-[#C9A84C] flex items-center justify-center">
                    <Icon size={22} strokeWidth={2.5} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-[#0B2545] font-bold">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#1F2A44] leading-relaxed font-medium">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Hiranandani Legacy */}
      {meta.legacy_stats && (
        <section
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20"
          data-testid="project-legacy"
        >
          <div className="relative overflow-hidden rounded-3xl border-2 border-[#C9A84C]/60 bg-[#0B2545] px-6 py-12 md:px-14 md:py-16">
            <div className="absolute inset-0 bg-gradient-to-br from-[#061528] via-[#0B2545] to-[#133763]" />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-bold">
                Developer Legacy
              </div>
              <h2 className="mt-2 font-serif text-3xl md:text-4xl text-white font-bold">
                {meta.legacy_stats.heading}
              </h2>
              <span className="rc-underline-gold" />
              <p className="mt-5 text-[#E6ECF5] max-w-2xl font-medium">
                {meta.legacy_stats.sub}
              </p>
              <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5">
                {meta.legacy_stats.items.map((s, i) => {
                  const Icon = LEGACY_ICONS[s.icon] || Award;
                  return (
                    <div
                      key={i}
                      data-testid={`legacy-stat-${i}`}
                      className="p-5 rounded-xl bg-white/5 border border-[#C9A84C]/30 backdrop-blur-sm hover:bg-white/10 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#C9A84C]/15 text-[#C9A84C] flex items-center justify-center">
                        <Icon size={18} strokeWidth={2.5} />
                      </div>
                      <div className="mt-4 font-serif text-3xl text-[#C9A84C] font-bold leading-none">
                        {s.value}
                      </div>
                      <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-white font-bold">
                        {s.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}


      {/* CTA */}
      <section className="bg-[#0B2545] mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 text-white">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#C9A84C] font-semibold">
              Talk to a Richcube advisor
            </div>
            <h3 className="mt-2 font-serif text-2xl md:text-3xl">
              Book a private walkthrough of {project.name}
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => openEnquiry({ title_en: project.name, id: null })}
              data-testid="project-cta-enquire"
              className="bg-[#C9A84C] text-[#0B2545] hover:bg-[#DFBF65] font-semibold uppercase tracking-wider text-xs h-11"
            >
              {t.detail.enquire_now}
            </Button>
            <a
              href={`https://wa.me/919203809569?text=${encodeURIComponent(
                `Richcube enquiry — ${project.name}`
              )}`}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="project-cta-whatsapp"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-md border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white font-semibold uppercase tracking-wider text-xs transition-colors"
            >
              <MessageCircle size={16} />
              WhatsApp 9203809569
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <EnquiryModal
        property={enquiryFor}
        open={!!enquiryFor}
        onClose={() => setEnquiryFor(null)}
      />
    </div>
  );
}
